/* eslint-disable react/require-default-props */
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { useState } from 'react';

import {
  convertDocumentStatus,
  DocumentStatus,
} from '../../../../../../api/enumerations';
import { NoticeDocumentsData } from '../../../../../../api/theHive/notices/types';
import {
  IconAttachFileMS,
  IconDescriptionMS,
} from '../../../../../../constants/icons';
import { formatDate } from '../../../../../../helpers';
import useGeneral from '../../../../../../hooks/useGeneral';
import {
  TableCellHeader,
  StyledCell,
  StyledRow,
  ButtonsWrapper,
  StatusCell,
  OutlinedButton,
  DownloadButton,
  ActionButton,
  StyledTooltip,
} from '../styles';
import { getDocumentStatusColor, stripHtml } from '../utils';
import { CancelDocumentDialog } from './CancelDocumentDialog';
import { RejectDialog } from './RejectDialog';
import { RejectReasonDialog } from './RejectionReason';

interface DocumentsTableProps {
  name: string;
  docList: NoticeDocumentsData[];
  docLoading: boolean;
  handleReplace: (docId: number) => Promise<void>;
  handleApprove: (docId?: number) => Promise<void>;
  handleReject: (reason: string, docId?: number) => Promise<void>;
  handleCancel?: (docId: number) => Promise<void>;
  disabled: boolean;
}

export default function DocumentsTable({
  name,
  docList,
  docLoading,
  handleReplace,
  handleApprove,
  handleReject,
  handleCancel,
  disabled,
}: DocumentsTableProps): JSX.Element {
  const [openRejectionDialog, setOpenRejectionDialog] = useState<
    NoticeDocumentsData | undefined
  >(undefined);

  const { handleDownloadFile } = useGeneral();

  const handleStatus = (doc: NoticeDocumentsData): JSX.Element => {
    switch (doc.status) {
      case DocumentStatus.PENDING:
        return (
          <CancelDocumentDialog
            docId={doc.id}
            name={doc.description}
            handleCancel={handleCancel}
            disabled={disabled}
          />
        );

      case DocumentStatus.UNDER_ANALYSIS:
        return (
          <>
            <RejectDialog
              docId={doc.id}
              name={doc.description}
              document={doc.file}
              docLoading={docLoading}
              handleReject={handleReject}
              disabled={disabled}
            />
            <Button
              disabled={docLoading || disabled}
              sx={{ maxWidth: '140px', height: '40px' }}
              onClick={() => handleApprove(doc.id)}
            >
              aprovar
            </Button>
          </>
        );

      case DocumentStatus.APPROVED:
        return (
          <RejectDialog
            docId={doc.id}
            name={doc.description}
            document={doc.file}
            docLoading={docLoading}
            handleReject={handleReject}
            disapprove
            disabled={disabled}
          />
        );

      case DocumentStatus.REJECTED:
        return (
          <OutlinedButton
            sx={{ maxWidth: '280px' }}
            onClick={() => handleReplace(doc.id)}
            disabled={disabled}
          >
            solicitar substituição
          </OutlinedButton>
        );

      case DocumentStatus.CANCELED:
        return (
          <OutlinedButton sx={{ maxWidth: '280px' }} disabled>
            cancelar
          </OutlinedButton>
        );

      case DocumentStatus.REPLACEMENT_REQUEST:
        return (
          <OutlinedButton sx={{ maxWidth: '280px' }} disabled>
            solicitar substituição
          </OutlinedButton>
        );
      default:
        return <Box />;
    }
  };

  const closeRejectionDialog = (): void => setOpenRejectionDialog(undefined);

  return (
    <Box my={5}>
      <Typography color="common.grey" fontSize={21} fontWeight={600} my={3}>
        {name}
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader sx={{ padding: '20px 10px', width: '3%' }}>
                {IconAttachFileMS}
              </TableCellHeader>
              <TableCellHeader width="17%">documento</TableCellHeader>
              <TableCellHeader align="center" width="15%">
                data de entrada
              </TableCellHeader>
              <TableCellHeader align="center" width="15%">
                status
              </TableCellHeader>
              <TableCellHeader align="center" width="20%">
                motivo
              </TableCellHeader>
              <TableCellHeader align="center" width="30%">
                ações
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {docList.length === 0 && (
              <StyledRow>
                <StyledCell align="center" colSpan={6}>
                  Documentos pendentes de envio
                </StyledCell>
              </StyledRow>
            )}
            {docList.map((item) => (
              <StyledRow key={item.id}>
                <StyledCell sx={{ padding: '20px 10px' }}>
                  <DownloadButton
                    disabled={!item.url}
                    onClick={() => handleDownloadFile(item.url, item.file)}
                  >
                    {IconDescriptionMS}
                  </DownloadButton>
                </StyledCell>
                <StyledCell>
                  <StyledTooltip title={item.description || ''} placement="top">
                    <Box>
                      <ActionButton
                        disabled={!item.url}
                        onClick={() => handleDownloadFile(item.url, item.file)}
                      >
                        <span>{item.description}</span>
                      </ActionButton>
                    </Box>
                  </StyledTooltip>
                </StyledCell>
                <StyledCell align="center">
                  {formatDate(item.sent_at)}
                </StyledCell>
                <StatusCell
                  align="center"
                  sx={{
                    color: getDocumentStatusColor(item.status),
                  }}
                >
                  {convertDocumentStatus(item.status)}
                </StatusCell>
                <StyledCell align="center">
                  <ActionButton
                    sx={{ textAlign: 'center' }}
                    onClick={() => setOpenRejectionDialog(item)}
                  >
                    <span>{stripHtml(item.rejection_reason)}</span>
                  </ActionButton>
                </StyledCell>
                <StyledCell>
                  <ButtonsWrapper>{handleStatus(item)}</ButtonsWrapper>
                </StyledCell>
              </StyledRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <RejectReasonDialog
        open={!!openRejectionDialog}
        closeDialog={closeRejectionDialog}
        rejectionReason={openRejectionDialog?.rejection_reason || ''}
        document={openRejectionDialog?.description || ''}
      />
    </Box>
  );
}
